import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { size } from 'lodash';
import { combineLatest, map, Subject } from 'rxjs';
import {
  forbiddenCursor,
  measureCursor,
} from '../../../cross-team-shared/cursors/custom-cursors';
import {
  getCssProperty,
  setCssProperty,
} from '../../../cross-team-shared/misc-utils';
import { pointCloudShapeTools } from '../../../cross-team-shared/tools-names';
import { EditMode3D } from '../../../cross-team-shared/types/tool-modes';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { PointCloudToolName } from '../../state/point-cloud-content-state';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { ObjectWith } from '../../utils/misc-utils';
import { MainPointCloudRendererComponent } from './main-point-cloud-renderer.component';

type DraggingStatus = 'waiting' | 'rotating' | 'panning' | 'completed';

@Injectable()
export class MainPointCloudRendererCursorService {
  draggingStatus$ = new Subject<DraggingStatus>();

  private store = inject(PointCloudContentStore);
  private shellConnector = inject(shellConnectorToken);
  private cursorBeforeDragging: string | null = null;

  manageCursor(mainPointCloudRenderer: MainPointCloudRendererComponent) {
    this.setUpCustomCursorSubscriptions(mainPointCloudRenderer);
    this.setUpDraggingCursorsSubscriptions(mainPointCloudRenderer);
  }

  private setUpCustomCursorSubscriptions(
    mainPointCloudRenderer: MainPointCloudRendererComponent,
  ) {
    mainPointCloudRenderer.subscribeTo(
      combineLatest([
        this.store('selectedTool').$,
        this.store('shapeCreationEnabled').$,
        this.store('render')('editMode').$,
        this.store('render')('selection').$,
      ]).pipe(
        map(([selectedTool, shapeCreationEnabled, editMode, selection]) =>
          this.getCursor(
            selectedTool,
            shapeCreationEnabled,
            editMode,
            selection,
          ),
        ),
      ),
      (cursor) => this.setCursor(cursor, mainPointCloudRenderer),
    );
  }

  private setUpDraggingCursorsSubscriptions(
    mainPointCloudRenderer: MainPointCloudRendererComponent,
  ) {
    mainPointCloudRenderer.subscribeTo(
      this.draggingStatus$.pipe(
        map((draggingStatus) => {
          switch (draggingStatus) {
            case 'waiting':
              this.cursorBeforeDragging = getCssProperty(
                mainPointCloudRenderer.mainViewRef,
                'cursor',
              );
              return 'grab';
            case 'panning':
              return 'grabbing';
            case 'rotating':
              return 'pointer';
            case 'completed':
              return this.cursorBeforeDragging;
          }
        }),
      ),
      (cursor) => {
        setCssProperty(mainPointCloudRenderer.mainViewRef, 'cursor', cursor);
      },
    );
  }

  private getCursor(
    selectedTool: PointCloudToolName | undefined,
    shapeCreationEnabled: boolean,
    editMode: EditMode3D,
    selection: ObjectWith<true>,
  ) {
    const shapeCreationDisabled = this.isShapeCreationDisabled(
      selectedTool,
      shapeCreationEnabled,
    );

    if (shapeCreationDisabled) {
      return forbiddenCursor;
    }

    const useNewEditPanel =
      this.shellConnector.hasFeatureFlag('new-edit-panel');

    return getCursorByTool(selectedTool, editMode, selection, useNewEditPanel);
  }

  private isShapeCreationDisabled(
    selectedTool: string | undefined,
    shapeCreationEnabled: boolean,
  ): boolean {
    const tools = pointCloudShapeTools as readonly string[];
    return !shapeCreationEnabled && tools.includes(selectedTool || '');
  }

  private setCursor(
    value: string | null,
    mainPointCloudRenderer: MainPointCloudRendererComponent,
  ) {
    if (isDefined(mainPointCloudRenderer.mainViewRef)) {
      setCssProperty(mainPointCloudRenderer.mainViewRef, 'cursor', value);
    }
  }
}

function getCursorByTool(
  tool: PointCloudToolName | undefined,
  editMode: EditMode3D,
  selection: ObjectWith<true>,
  useNewEditPanel: boolean,
) {
  switch (tool) {
    case 'Select':
      return 'default';

    case 'Edit':
      return getEditCursorByMode(editMode, selection, useNewEditPanel);

    case 'Cuboid':
      return 'crosshair';

    case 'Measure':
      return measureCursor;

    default:
      return null;
  }
}

// eslint-disable-next-line complexity -- This function is complex by design
function getEditCursorByMode(
  mode: EditMode3D,
  selection: ObjectWith<true>,
  useNewEditPanel: boolean,
) {
  if (size(selection) !== 1) {
    return 'default';
  }

  switch (mode) {
    case 'rotate':
      return 'pointer';

    case 'translate':
      return 'move';

    case 'select face':
      return useNewEditPanel ? forbiddenCursor : 'default';

    case 'resize':
      throw new Error('Invalid Edit mode: resize');
  }
}
